// Plays the song recording through a Tone.Player (Web Audio clock) so the cursor is sample-accurate
// to what's heard. Ported from poc/web/app.js (getPlaybackTime/playSong/pauseSong/seekTo).
import * as Tone from "tone";
import type { PlaybackSource } from "./PlaybackSource";

export class SongController implements PlaybackSource {
  private player: Tone.Player | null = null;
  private baseT = 0;        // song position (s) captured at last start/seek/pause
  private ctxStart = 0;     // Tone context time at the last start
  private running = false;
  private cursorComp = 0.1; // extra cursor delay (s), dialed by ear
  private volume = 100;     // 0..100
  loadedUrl: string | null = null;
  whenLoaded: Promise<void> = Promise.resolve();

  load(url: string): Promise<void> {
    if (this.player) { try { this.player.dispose(); } catch { /* noop */ } this.player = null; }
    this.baseT = 0; this.running = false; this.loadedUrl = url;
    this.whenLoaded = new Promise<void>((resolve) => {
      this.player = new Tone.Player({ url, onload: () => resolve() }).toDestination();
      this.applyVolume();
    });
    return this.whenLoaded;
  }

  private applyVolume(): void {
    if (!this.player) return;
    this.player.volume.value = this.volume <= 0 ? -Infinity : 20 * Math.log10(this.volume / 100);
  }
  setVolume(v: number): void { this.volume = v; this.applyVolume(); }
  setCursorComp(seconds: number): void { this.cursorComp = seconds; }
  get loaded(): boolean { return !!this.player?.loaded; }
  get duration(): number { return this.player?.buffer?.duration ?? 0; }

  getPlaybackTime(): number {
    if (!this.player) return 0;
    if (!this.running) return this.baseT;
    const ctx = Tone.getContext();
    const raw: any = (ctx && (ctx as any).rawContext) ? (ctx as any).rawContext : ctx;
    const lat = ((raw && (raw.outputLatency || raw.baseLatency)) || 0) + this.cursorComp;
    return this.baseT + Math.max(0, ctx.currentTime - this.ctxStart - lat);
  }

  async play(): Promise<void> {
    if (!this.player || !this.player.loaded) return;
    await Tone.start();
    try { this.player.stop(); } catch { /* noop */ }
    const startAt = Tone.now();   // scheduled start = ctx time + lookAhead; cursor uses this
    try { this.player.start(startAt, Math.max(0, this.baseT)); } catch { /* noop */ }
    this.ctxStart = startAt;
    this.running = true;
  }

  pause(): void {
    if (!this.player) return;
    this.baseT = this.getPlaybackTime();
    try { this.player.stop(); } catch { /* noop */ }
    this.running = false;
  }

  seek(t: number): void {
    this.baseT = Math.max(0, t);
    if (this.running && this.player) {
      try { this.player.stop(); } catch { /* noop */ }
      const startAt = Tone.now();
      try { this.player.start(startAt, this.baseT); this.ctxStart = startAt; } catch { /* noop */ }
    }
  }

  setBaseTime(t: number): void { this.baseT = Math.max(0, t); }
  seekSilent(t: number): void { this.baseT = Math.max(0, t); }   // move position; play() resumes from here
  isPlaying(): boolean { return this.running; }

  dispose(): void {
    try { this.player?.dispose(); } catch { /* noop */ }
    this.player = null; this.running = false;
  }
}
